import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useContent, useEntry, useSaveEntry } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { Btn, Overline } from "@/components/kit";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "@/components/ui/sonner";
import { Map, ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

function PromptField({ p, value, onChange, onBlur }) {
  return (
    <div className="mb-6" data-testid={`prompt-${p.id}`}>
      <label className="block text-ink font-medium mb-1" htmlFor={p.id}>{p.label}</label>
      {p.help && <p className="text-inkfaint text-sm mb-2 leading-relaxed">{p.help}</p>}
      {p.type === "long" ? (
        <textarea id={p.id} value={value} onChange={(e) => onChange(p.id, e.target.value)} onBlur={onBlur}
          placeholder="Write from here…" data-testid={`input-${p.id}`}
          className="w-full bg-panel border border-line rounded-xl px-4 py-3 text-ink placeholder:text-faint focus:border-gold transition-colors min-h-[120px] leading-relaxed resize-y" />
      ) : (
        <input id={p.id} value={value} onChange={(e) => onChange(p.id, e.target.value)} onBlur={onBlur}
          placeholder="Write from here…" data-testid={`input-${p.id}`}
          className="w-full bg-panel border border-line rounded-xl px-4 py-3 text-ink placeholder:text-faint focus:border-gold transition-colors" />
      )}
    </div>
  );
}

export default function Journal() {
  const { id } = useParams();
  const mid = parseInt(id, 10);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: content } = useContent();
  const { data: entry } = useEntry(mid);
  const save = useSaveEntry(mid);

  const state = content?.states.find((s) => s.id === mid);
  const draftKey = user ? `emergence_draft_${user.id}_${mid}` : null;

  const [answers, setAnswers] = useState(null);
  const [mode, setMode] = useState(localStorage.getItem("journal_mode") || "scroll");
  const [gIndex, setGIndex] = useState(0);
  const [status, setStatus] = useState("idle");
  const [recovery, setRecovery] = useState(null);
  const timer = useRef(null);
  const answersRef = useRef(null);
  const initGuided = useRef(false);

  const flat = useMemo(
    () => (state ? state.sections.flatMap((s) => s.prompts.map((p) => ({ ...p, sectionTitle: s.title }))) : []),
    [state]
  );

  useEffect(() => { answersRef.current = answers; }, [answers]);

  // init from server + draft compare
  useEffect(() => {
    if (!entry || answers !== null || !state) return;
    let draft = null;
    try { draft = draftKey && JSON.parse(localStorage.getItem(draftKey) || "null"); } catch {}
    const serverTime = entry.updated_at ? new Date(entry.updated_at).getTime() : 0;
    const server = entry.answers || {};
    if (draft?.updatedAt && new Date(draft.updatedAt).getTime() > serverTime &&
        JSON.stringify(draft.answers) !== JSON.stringify(server)) {
      setAnswers(server);
      setRecovery(draft.answers);
    } else {
      setAnswers(server);
      if (draft && draftKey) localStorage.removeItem(draftKey);
    }
  }, [entry, state, answers, draftKey]);

  // resume guided at first unanswered
  useEffect(() => {
    if (answers && !initGuided.current && flat.length) {
      initGuided.current = true;
      const i = flat.findIndex((p) => !(answers[p.id] || "").trim());
      setGIndex(i < 0 ? 0 : i);
    }
  }, [answers, flat]);

  // flush on unmount
  useEffect(() => () => {
    if (timer.current && answersRef.current) {
      save.mutate({ answers: answersRef.current });
    }
  }, []); // eslint-disable-line

  const persistDraft = (next) => {
    if (draftKey) localStorage.setItem(draftKey, JSON.stringify({ answers: next, updatedAt: new Date().toISOString() }));
  };

  const doSave = (next, completed) => {
    setStatus("saving");
    const payload = { answers: next };
    if (completed !== undefined) payload.completed = completed;
    save.mutate(payload, {
      onSuccess: () => { setStatus("saved"); if (draftKey) localStorage.removeItem(draftKey); },
      onError: () => setStatus(navigator.onLine ? "error" : "offline"),
    });
  };

  const onChange = (pid, val) => {
    setAnswers((prev) => {
      const next = { ...prev, [pid]: val };
      persistDraft(next);
      setStatus("saving");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => doSave(next), 1200);
      return next;
    });
  };

  const flush = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (answersRef.current) doSave(answersRef.current);
  };

  const completeState = () => {
    if (timer.current) clearTimeout(timer.current);
    setStatus("saving");
    save.mutate({ answers: answersRef.current || {}, completed: true }, {
      onSuccess: () => { setStatus("saved"); if (draftKey) localStorage.removeItem(draftKey); toast.success(`${state.me} marked complete.`); navigate(`/month/${mid}`); },
      onError: () => setStatus(navigator.onLine ? "error" : "offline"),
    });
  };

  const restoreDraft = () => {
    const merged = { ...answers, ...recovery };
    setAnswers(merged); persistDraft(merged); doSave(merged); setRecovery(null);
    toast.success("Your draft has been restored.");
  };
  const discardDraft = () => { if (draftKey) localStorage.removeItem(draftKey); setRecovery(null); };

  const switchMode = (m) => { setMode(m); localStorage.setItem("journal_mode", m); };

  if (!content || !state || answers === null) {
    return <div className="text-inkfaint text-sm animate-pulse">Opening your journal…</div>;
  }

  const total = flat.length;
  const answered = flat.filter((p) => (answers[p.id] || "").trim()).length;
  const completed = entry?.completed;

  const CompletionArea = () => (
    <div className="mt-8 border-t border-line pt-6">
      <p className="text-inkfaint text-sm mb-4">
        {completed
          ? "You have marked this state complete. You can keep reflecting any time."
          : "When you have lived this month and reflected, mark this state complete to move forward."}
      </p>
      {completed ? (
        <Btn variant="outline" onClick={flush} data-testid="save-reflections-btn">Save reflections</Btn>
      ) : (
        <Btn onClick={completeState} data-testid="complete-state-btn">Complete {state.me}</Btn>
      )}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto em-fade-up pb-28" data-testid="journal-page">
      {/* header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Overline>{state.me}</Overline>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-1">{state.theme}</h1>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={status} />
          <button onClick={() => navigate("/reference")} data-testid="journal-map-btn"
            className="inline-flex items-center gap-1.5 text-sm text-inkfaint hover:text-ink">
            <Map className="w-4 h-4" /> Reference
          </button>
        </div>
      </div>

      {/* mode toggle */}
      <div className="inline-flex bg-panel border border-line rounded-full p-1 mt-6">
        {["scroll", "guided"].map((m) => (
          <button key={m} onClick={() => switchMode(m)} data-testid={`mode-${m}`}
            className={`px-6 py-2 rounded-full text-sm capitalize transition-colors ${mode === m ? "bg-golddark text-gold" : "text-inkfaint hover:text-ink"}`}>
            {m}
          </button>
        ))}
      </div>

      {recovery && (
        <div className="mt-6 bg-panel border border-emwarning/50 rounded-2xl p-4 flex items-start gap-3" data-testid="recovery-banner">
          <RotateCcw className="w-5 h-5 text-emwarning mt-0.5" />
          <div className="flex-1">
            <div className="text-ink font-semibold">Unsaved writing recovered</div>
            <p className="text-inkfaint text-sm mt-1">We found more recent writing saved on this device than on the server. Would you like to restore it?</p>
            <div className="flex gap-4 mt-3">
              <button onClick={restoreDraft} className="text-gold text-sm hover:underline" data-testid="recovery-restore">Restore my draft</button>
              <button onClick={discardDraft} className="text-faint text-sm hover:text-inkfaint" data-testid="recovery-discard">Discard</button>
            </div>
          </div>
        </div>
      )}

      {mode === "scroll" ? (
        <div className="mt-8">
          {/* chips */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
            <button onClick={() => navigate("/reference?chart=architecture")} className="shrink-0 px-4 py-2 rounded-full bg-panel border border-line text-sm text-inkfaint hover:text-ink" data-testid="chip-architecture">Architecture</button>
            <button onClick={() => navigate("/reference?chart=modes")} className="shrink-0 px-4 py-2 rounded-full bg-panel border border-line text-sm text-inkfaint hover:text-ink" data-testid="chip-modes">12 Modes</button>
            {state.sections.map((s) => (
              <button key={s.id} onClick={() => document.getElementById(`sec-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="shrink-0 px-4 py-2 rounded-full bg-panel border border-line text-sm text-inkfaint hover:text-ink">
                {s.title}
              </button>
            ))}
          </div>

          {state.sections.map((s) => (
            <section key={s.id} id={`sec-${s.id}`} className="mt-10 scroll-mt-20">
              <h2 className="font-serif text-2xl text-gold">{s.title}</h2>
              {s.stopBefore && (
                <div className="mt-2 text-xs tracking-widest uppercase text-emwarning border border-emwarning/40 rounded-lg px-3 py-2">
                  {s.stopBefore}
                </div>
              )}
              {s.note && <p className="text-inkfaint text-sm mt-2 mb-4 leading-relaxed">{s.note}</p>}
              <div className="mt-4">
                {s.prompts.map((p) => (
                  <PromptField key={p.id} p={p} value={answers[p.id] || ""} onChange={onChange} onBlur={flush} />
                ))}
              </div>
            </section>
          ))}
          <CompletionArea />
        </div>
      ) : (
        <div className="mt-8">
          <div className="h-1.5 bg-panel2 rounded-full overflow-hidden">
            <div className="h-full bg-gold transition-all" style={{ width: `${((gIndex + 1) / total) * 100}%` }} />
          </div>
          <div className="text-xs text-faint mt-2">{gIndex + 1} of {total}</div>

          <div className="mt-8 min-h-[240px]">
            <Overline>{flat[gIndex].sectionTitle}</Overline>
            <div className="mt-3">
              <PromptField p={flat[gIndex]} value={answers[flat[gIndex].id] || ""} onChange={onChange} onBlur={flush} />
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <Btn variant="ghost" disabled={gIndex === 0} onClick={() => { flush(); setGIndex((i) => Math.max(0, i - 1)); }} data-testid="guided-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Btn>
            {gIndex < total - 1 ? (
              <Btn onClick={() => { flush(); setGIndex((i) => Math.min(total - 1, i + 1)); }} data-testid="guided-next">
                Next <ArrowRight className="w-4 h-4" />
              </Btn>
            ) : (
              <Btn onClick={() => { flush(); navigate(`/month/${mid}`); }} data-testid="guided-done">Done</Btn>
            )}
          </div>

          {gIndex === total - 1 && <CompletionArea />}
        </div>
      )}

      {/* sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 bg-surface/90 backdrop-blur border-t border-line z-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <span className="text-sm text-inkfaint" data-testid="answered-count">{answered}/{total} answered</span>
          <div className="flex items-center gap-3">
            <StatusPill status={status} />
            <Btn variant="outline" className="h-11 px-5" onClick={flush} data-testid="save-progress-btn">Save progress</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
